import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useOmnipresentAI } from '../../hooks/useOmnipresentAI';
import { useOmnipresentAIStore, useAIConversations, useCurrentConversationId, useAIActions } from '../../stores';
import { createPortal } from 'react-dom';
import { StreamdownRenderer } from '../common/StreamdownRenderer';
import type { Conversation } from '../../stores';

// Helper to format timestamp
const formatTimestamp = (timestamp: number) => {
  const date = new Date(timestamp);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isYesterday) {
    return `Yesterday ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
  }

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
};

export const AdaptiveChat: React.FC = () => {
  const {
    mode,
    interactionMode,
    messages,
    inputValue,
    setInputValue,
    sendMessage,
    deleteMessage: deleteMessageFromStore,
    isProcessing,
    minimize,
    expandToSidebar,
    expandToFullscreen,
    toggleMode,
    setInteractionMode,
    currentUser,
  } = useOmnipresentAI();

  const sidebarWidth = useOmnipresentAIStore((state) => state.sidebarWidth);
  const setSidebarWidth = useOmnipresentAIStore((state) => state.setSidebarWidth);
  const conversations = useAIConversations();
  const currentConversationId = useCurrentConversationId();
  const conversationActions = useAIActions();

  const [isResizing, setIsResizing] = useState(false);
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [showConversationList, setShowConversationList] = useState(false);
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Copy message to clipboard
  const copyMessage = async (content: string, messageId: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy message:', err);
    }
  };

  // Delete message
  const deleteMessage = (messageId: string) => {
    if (confirm('Delete this message?')) {
      deleteMessageFromStore(messageId);
    }
  };

  // Regenerate response (stub for now)
  const regenerateResponse = () => {
    // TODO: Implement regenerate
    console.log('Regenerate response');
  };

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isProcessing]);

  // Focus input when opening
  useEffect(() => {
    if (mode !== 'hidden') {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [mode]);

  // Handle enter key to send
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle resize drag
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = window.innerWidth - e.clientX;
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, setSidebarWidth]);

  // Prevent rendering if hidden, but AnimatePresence handles exit animations
  // so we keep it mounted but hidden/null? No, AnimatePresence wraps conditionally rendered component.

  return createPortal(
    <AnimatePresence mode="wait">
      {mode !== 'hidden' && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={minimize}
            className={`fixed inset-0 z-[9998] ${
              mode === 'fullscreen' ? 'bg-black/60 backdrop-blur-sm' : 'bg-transparent'
            }`}
          />

          <motion.div
            key="chat-container"
            initial={mode === 'sidebar' ? { x: '100%', opacity: 0 } : { scale: 0.9, opacity: 0 }}
            animate={mode === 'sidebar' ? { x: 0, opacity: 1 } : { scale: 1, opacity: 1 }}
            exit={mode === 'sidebar' ? { x: '100%', opacity: 0 } : { scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            style={mode === 'sidebar' ? { width: `${sidebarWidth}px` } : undefined}
            className={`fixed z-[9999] bg-[#0A0A0A] border border-white/10 shadow-2xl flex flex-col overflow-hidden
              ${mode === 'sidebar'
                ? 'top-0 right-0 h-full border-l'
                : 'top-[10%] left-[10%] w-[80%] h-[80%] rounded-2xl'
              }`}
          >
            {/* Resize Handle (only for sidebar mode) */}
            {mode === 'sidebar' && (
              <div
                onMouseDown={() => setIsResizing(true)}
                className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-[#5260FF] transition-colors z-[10000] group"
              >
                <div className="absolute left-0 top-0 bottom-0 w-4 -translate-x-1.5 opacity-0 group-hover:opacity-100 bg-[#5260FF]/20 transition-opacity" />
              </div>
            )}
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/5 bg-white/5 backdrop-blur-md">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowConversationList(!showConversationList)}
                  className="p-1.5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors"
                  title="Conversations"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </button>
                <button
                  onClick={() => conversationActions.createNewConversation()}
                  className="p-1.5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors"
                  title="New conversation"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>

              {/* Interaction Mode Toggle */}
              <div className="flex items-center bg-[#1A1A1A] rounded-full p-1 border border-white/10">
                <button
                  onClick={() => setInteractionMode('conversational')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                    interactionMode === 'conversational'
                      ? 'bg-white text-black shadow-lg'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Conversational
                </button>
                <button
                  onClick={() => setInteractionMode('document')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                    interactionMode === 'document'
                      ? 'bg-white text-black shadow-lg'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Document
                </button>
              </div>

              <div className="flex items-center gap-2">
                {mode === 'sidebar' ? (
                  <button onClick={expandToFullscreen} className="p-1.5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors" title="Expand">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
                  </button>
                ) : (
                  <button onClick={expandToSidebar} className="p-1.5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors" title="Sidebar">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" /></svg>
                  </button>
                )}
                <button onClick={minimize} className="p-1.5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors" title="Close">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            {/* Conversation List Sidebar */}
            <AnimatePresence>
              {showConversationList && (
                <motion.div
                  initial={{ x: -300, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: -300, opacity: 0 }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="absolute left-0 top-[57px] bottom-0 w-[280px] bg-[#0A0A0A] border-r border-white/10 z-10 overflow-y-auto custom-scrollbar"
                >
                  <div className="p-3 space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Conversations</h4>
                      <span className="text-[10px] text-gray-600">{conversations.length}</span>
                    </div>

                    {conversations.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-2 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        <p className="text-xs">No conversations yet</p>
                      </div>
                    ) : (
                      conversations.map((conversation: Conversation) => (
                        <div
                          key={conversation.id}
                          className={`group relative p-3 rounded-lg border transition-all cursor-pointer ${
                            currentConversationId === conversation.id
                              ? 'bg-[#5260FF]/10 border-[#5260FF]/30'
                              : 'bg-white/5 border-white/5 hover:bg-white/10 hover:border-white/10'
                          }`}
                          onClick={() => {
                            conversationActions.loadConversation(conversation.id);
                            setShowConversationList(false);
                          }}
                        >
                          {editingConversationId === conversation.id ? (
                            <input
                              type="text"
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              onBlur={() => {
                                if (editingTitle.trim()) {
                                  conversationActions.renameConversation(conversation.id, editingTitle.trim());
                                }
                                setEditingConversationId(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  if (editingTitle.trim()) {
                                    conversationActions.renameConversation(conversation.id, editingTitle.trim());
                                  }
                                  setEditingConversationId(null);
                                } else if (e.key === 'Escape') {
                                  setEditingConversationId(null);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              autoFocus
                              className="w-full bg-transparent border-none outline-none text-sm font-medium text-white"
                            />
                          ) : (
                            <h5 className="text-sm font-medium text-white mb-1 line-clamp-1">
                              {conversation.title}
                            </h5>
                          )}
                          {conversation.preview && (
                            <p className="text-xs text-gray-400 line-clamp-2 mb-2">
                              {conversation.preview}
                            </p>
                          )}
                          <div className="flex items-center justify-between text-[10px] text-gray-500">
                            <span>{new Date(conversation.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                            <span>{conversation.messages.length} messages</span>
                          </div>

                          {/* Action buttons */}
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingConversationId(conversation.id);
                                setEditingTitle(conversation.title);
                              }}
                              className="p-1 bg-[#0A0A0A] hover:bg-white/10 rounded text-gray-400 hover:text-white transition-colors"
                              title="Rename"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (confirm('Delete this conversation?')) {
                                  conversationActions.deleteConversation(conversation.id);
                                }
                              }}
                              className="p-1 bg-[#0A0A0A] hover:bg-red-500/20 rounded text-gray-400 hover:text-red-400 transition-colors"
                              title="Delete"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Messages Area */}
            <div className={`flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar bg-black/20 ${
              interactionMode === 'document' ? 'px-10' : 'px-14'
            }`}>
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 space-y-6">
                  <div className="w-50 h-50 rounded-full overflow-hidden shadow-lg ring-2 ring-[#5260FF]/30 shadow-[0_0_30px_rgba(82,96,255,0.1)]">
                    <img
                      src="https://appwrite-prod-u12020.vm.elestio.app/v1/storage/buckets/6940e6c3002eddae9d6b/files/chat-avatar/view?project=693c0579001e8b06c6d6"
                      alt="Brainiacs AI"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <h4 className="text-white font-medium text-lg">Campaign Assistant</h4>
                      <span className="px-1.5 py-0.5 rounded-md text-[9px] font-black bg-amber-500/20 text-amber-400 border border-amber-500/30">
                        BETA
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 max-w-xs mx-auto">I'll help you create targeted marketing campaigns. Let's get started!</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 w-full max-w-sm">
                    {['Create new campaign', 'Lead generation campaign', 'Book appointments', 'Schedule product demo'].map(q => (
                      <button
                        key={q}
                        onClick={() => sendMessage(q)}
                        className="text-xs px-4 py-3 bg-white/5 hover:bg-white/10 hover:text-white rounded-xl border border-white/5 hover:border-[#5260FF]/30 transition-all text-left text-gray-400"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              ) : interactionMode === 'conversational' ? (
                // Conversational Mode: Traditional chat bubbles (user right, AI left)
                messages.map((msg) => (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    key={msg.id}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-5 py-3.5 text-sm shadow-lg ${
                        msg.role === 'user'
                          ? 'bg-[#5260FF] text-white rounded-br-none shadow-[#5260FF]/20'
                          : 'bg-[#1A1A1A] text-gray-200 rounded-bl-none border border-white/10'
                      }`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-white/5">
                          <span className="font-bold text-xs text-[#5260FF] uppercase tracking-wider">Brainiacs AI</span>
                          {msg.context && (
                            <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/5 text-[10px] text-gray-400">
                              {msg.context.type}
                            </span>
                          )}
                        </div>
                      )}
                      {msg.role === 'assistant' ? (
                        <StreamdownRenderer content={msg.content} mode="static" />
                      ) : (
                        <div className="whitespace-pre-wrap leading-relaxed">{msg.content}</div>
                      )}
                    </div>
                  </motion.div>
                ))
              ) : (
                // Document Mode: Linear layout with avatars on the left for all messages
                messages.map((msg, index) => (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    key={msg.id}
                    className="flex gap-3 items-start group relative"
                    onMouseEnter={() => setHoveredMessageId(msg.id)}
                    onMouseLeave={() => setHoveredMessageId(null)}
                  >
                    {/* Avatar */}
                    <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden shadow-lg ring-2 ring-[#5260FF]/20">
                      {msg.role === 'assistant' ? (
                        <img
                          src="https://appwrite-prod-u12020.vm.elestio.app/v1/storage/buckets/6940e6c3002eddae9d6b/files/chat-avatar/view?project=693c0579001e8b06c6d6&mode=admin"
                          alt="Brainiacs AI"
                          className="w-full h-full object-cover"
                        />
                      ) : currentUser?.photoURL ? (
                        <img
                          src={currentUser.photoURL}
                          alt={currentUser.displayName || 'User'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#5260FF] to-cyan-400 flex items-center justify-center text-white text-xs font-bold">
                          {currentUser?.displayName?.[0]?.toUpperCase() || currentUser?.email?.[0]?.toUpperCase() || 'U'}
                        </div>
                      )}
                    </div>

                    {/* Message Content */}
                    <div className="flex-1 min-w-0 relative">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-xs text-white">
                          {msg.role === 'assistant' ? 'Brainiacs AI' : 'You'}
                        </span>
                        <span className="text-[10px] text-gray-500">
                          {formatTimestamp(msg.timestamp)}
                        </span>
                        {msg.error && (
                          <span className="px-2 py-0.5 rounded-full bg-red-500/20 border border-red-500/40 text-[10px] text-red-400 flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Failed
                          </span>
                        )}
                        {msg.context && (
                          <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/5 text-[10px] text-gray-400">
                            {msg.context.type}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-200 leading-relaxed">
                        {msg.role === 'assistant' ? (
                          <StreamdownRenderer content={msg.content} mode="static" />
                        ) : (
                          <div className="whitespace-pre-wrap">{msg.content}</div>
                        )}
                      </div>

                      {/* Action Buttons - Show on Hover or Always for Failed Messages */}
                      <div className={`absolute -right-2 top-0 transition-opacity flex items-center gap-1 ${msg.error ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                        {msg.error && msg.role === 'user' ? (
                          <button
                            onClick={() => {
                              deleteMessageFromStore(msg.id);
                              sendMessage(msg.content);
                            }}
                            className="p-1.5 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 hover:text-red-300 transition-colors"
                            title="Retry sending"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => copyMessage(msg.content, msg.id)}
                              className="p-1.5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors"
                              title={copiedMessageId === msg.id ? 'Copied!' : 'Copy'}
                            >
                              {copiedMessageId === msg.id ? (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              )}
                            </button>
                            {msg.role === 'assistant' && index === messages.length - 1 && (
                              <button
                                onClick={regenerateResponse}
                                className="p-1.5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors"
                                title="Regenerate"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                              </button>
                            )}
                          </>
                        )}
                        <button
                          onClick={() => deleteMessage(msg.id)}
                          className="p-1.5 hover:bg-white/10 rounded-lg text-gray-400 hover:text-red-400 transition-colors"
                          title="Delete"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}

              {/* Typing Indicator */}
              {isProcessing && (
                interactionMode === 'conversational' ? (
                  <div className="flex justify-start">
                    <div className="bg-[#1A1A1A] rounded-2xl rounded-bl-none px-4 py-3 border border-white/10 flex items-center gap-1.5 opacity-80">
                      <div className="w-1.5 h-1.5 bg-[#5260FF] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-1.5 h-1.5 bg-[#5260FF] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-1.5 h-1.5 bg-[#5260FF] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3 items-start">
                    {/* Avatar */}
                    <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden shadow-lg ring-2 ring-[#5260FF]/20">
                      <img
                        src="https://appwrite-prod-u12020.vm.elestio.app/v1/storage/buckets/6940e6c3002eddae9d6b/files/chat-avatar/view?project=693c0579001e8b06c6d6"
                        alt="Brainiacs AI"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-xs text-white">Brainiacs AI</span>
                      </div>
                      <div className="flex items-center gap-1.5 opacity-80">
                        <div className="w-1.5 h-1.5 bg-[#5260FF] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-1.5 h-1.5 bg-[#5260FF] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-1.5 h-1.5 bg-[#5260FF] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-white/10 bg-[#0A0A0A]">
              <div className="relative group">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask anything..."
                  rows={1}
                  className="w-full bg-[#121212] border border-white/10 rounded-xl px-4 py-3.5 pr-12 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#5260FF]/50 focus:bg-[#161616] focus:shadow-[0_0_20px_rgba(82,96,255,0.1)] resize-none transition-all scrollbar-hide"
                  style={{
                    minHeight: '50px',
                    maxHeight: '150px',
                    height: inputValue ? Math.min(150, Math.max(50, inputValue.split('\n').length * 20 + 30)) + 'px' : '50px'
                  }}
                />
                {isProcessing ? (
                  <button
                    onClick={() => {
                      // TODO: Implement stop generation
                      console.log('Stop generation');
                    }}
                    className="absolute right-2 bottom-2 p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-all shadow-lg"
                    title="Stop generating"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                ) : (
                  <button
                    onClick={() => sendMessage()}
                    disabled={!inputValue.trim()}
                    className="absolute right-2 bottom-2 p-2 bg-[#5260FF] hover:bg-[#4250ee] disabled:opacity-50 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-all shadow-lg shadow-[#5260FF]/20"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between mt-3 px-1">
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-gray-500 flex items-center gap-1.5">
                    <kbd className="min-w-[1.5em] px-1 py-0.5 bg-white/5 rounded border border-white/10 font-mono text-center">⏎</kbd> send
                  </span>
                  <span className="text-[10px] text-gray-500 flex items-center gap-1.5">
                    <kbd className="min-w-[1.5em] px-1 py-0.5 bg-white/5 rounded border border-white/10 font-mono text-center">Esc</kbd> close
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-gray-600">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500/50"></div>
                  Online
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};
